import api from "./api";

// ===== METAL RATE MANAGEMENT =====
const setRate = async (rateData) => {
  const response = await api.post("/admin-rates/set", rateData);
  return response.data;
};

const setManualRateWithCharges = async (rateData) => {
  const response = await api.post("/admin-rates/manual", rateData);
  return response.data;
};

const getRateDashboard = async (params = {}) => {
  const response = await api.get("/admin-rates/dashboard", { params });
  return response.data;
};

const approveRate = async (rateId, approvalData) => {
  const response = await api.post(
    `/admin-rates/approve/${rateId}`,
    approvalData
  );
  return response.data;
};

const getRateAnalytics = async (params) => {
  const response = await api.get("/admin-rates/analytics", { params });
  return response.data;
};

// ===== DAILY RATE MANAGEMENT =====
const setDailyRate = async (rateData) => {
  const response = await api.post("/admin-rates/daily/set", {
    ...rateData,
    branchCode: rateData.branchCode, // Ensure branchCode is included
  });
  return response.data;
};

const fetchFromAPI = async (params = {}) => {
  const response = await api.get("/admin-rates/daily/fetch-api", {
    params: {
      ...params,
      branchCode: params.branchCode, // Include branchCode in API fetch
    },
  });
  return response.data;
};

const confirmAPIRates = async (confirmData) => {
  const response = await api.post(
    "/admin-rates/daily/confirm-api",
    confirmData
  );
  return response.data;
};

const scheduleRate = async (scheduleData) => {
  const response = await api.post("/admin-rates/daily/schedule", scheduleData);
  return response.data;
};

const copyPreviousDay = async (copyData) => {
  const response = await api.post("/admin-rates/daily/copy", copyData);
  return response.data;
};

const getTodayRate = async (params = {}) => {
  const response = await api.get("/admin-rates/daily/today", {
    params: {
      ...params,
      branchCode: params.branchCode,
      date: params.date,
    },
  });
  return response.data;
};

const getRateStatus = async (params = {}) => {
  const response = await api.get("/admin-rates/daily/status", { params });
  return response.data;
};

const getRateHistory = async (params = {}) => {
  const response = await api.get("/admin-rates/daily/history", { params });
  return response.data;
};

const deleteRate = async (rateId) => {
  const response = await api.delete(`/admin-rates/daily/${rateId}`);
  return response.data;
};

const updateRate = async (rateId, updateData) => {
  const response = await api.put(`/admin-rates/daily/${rateId}`, updateData);
  return response.data;
};

const exportRates = async (params = {}) => {
  const response = await api.get("/admin-rates/daily/export", {
    params,
    responseType: params.format === "json" ? "json" : "blob",
  });
  return response.data;
};

const morningCheck = async (params = {}) => {
  const response = await api.get("/admin-rates/daily/morning-check", {
    params: {
      branchCode: params.branchCode,
    },
  });
  return response.data;
};

const bulkUpdateRates = async (updates) => {
  const response = await api.post("/admin-rates/daily/bulk", { updates });
  return response.data;
};

// ===== BRANCH/STORE CONFIGURATION =====
const configureRateMode = async (branchCode, modeData) => {
  const response = await api.put(
    `/admin-rates/config/mode/${branchCode}`,
    modeData
  );
  return response.data;
};

const configureStoreModeRate = async (storeCode, modeData) => {
  const response = await api.put(
    `/admin-rates/config/mode/store/${storeCode}`,
    modeData
  );
  return response.data;
};

const setMainBranch = async (branchCode) => {
  const response = await api.post("/admin-rates/branch/main", { branchCode });
  return response.data;
};

const configureBranchInheritance = async (branchCode, inheritanceData) => {
  const response = await api.put(
    `/admin-rates/branch/${branchCode}/inheritance`,
    inheritanceData
  );
  return response.data;
};

const syncRatesFromMain = async (branchCode) => {
  const response = await api.post(`/admin-rates/branch/${branchCode}/sync`);
  return response.data;
};

const copyRates = async (copyData) => {
  const response = await api.post("/admin-rates/copy", copyData);
  return response.data;
};

// Branch/Store Initialization
const initializeBranchRates = async (initData) => {
  const response = await api.post("/admin-rates/branch/initialize", initData);
  return response.data;
};

const syncBranchConfig = async (configData) => {
  const response = await api.post(
    "/admin-rates/branch/sync-config",
    configData
  );
  return response.data;
};

// ===== CHARGES CONFIGURATION =====
const setGSTRate = async (gstData) => {
  const response = await api.post("/admin-rates/gst", gstData);
  return response.data;
};

const setCustomCharge = async (chargeData) => {
  const response = await api.post("/admin-rates/custom-charge", chargeData);
  return response.data;
};

const getChargesConfig = async (params = {}) => {
  const response = await api.get("/admin-rates/charges-config", { params });
  return response.data;
};

// ===== COMPREHENSIVE CALCULATIONS =====
const calculateProductValue = async (calculationData) => {
  const response = await api.post("/admin-rates/calculate", calculationData);
  return response.data;
};

const getAllRatesWithCharges = async (params = {}) => {
  const response = await api.get("/admin-rates/all", { params });
  return response.data;
};

const rateService = {
  // Metal Rate Management
  setRate,
  setManualRateWithCharges,
  getRateDashboard,
  approveRate,
  getRateAnalytics,

  // Daily Rate Management
  setDailyRate,
  fetchFromAPI,
  confirmAPIRates,
  scheduleRate,
  copyPreviousDay,
  getTodayRate,
  morningCheck,
  bulkUpdateRates,
  getRateStatus,
  getRateHistory,
  deleteRate,
  updateRate,
  exportRates,

  // Branch/Store Configuration
  configureRateMode,
  configureStoreModeRate,
  setMainBranch,
  configureBranchInheritance,
  syncRatesFromMain,
  copyRates,
  initializeBranchRates,
  syncBranchConfig,

  // Charges Configuration
  setGSTRate,
  setCustomCharge,
  getChargesConfig,

  // Comprehensive Calculations
  calculateProductValue,
  getAllRatesWithCharges,
};

export default rateService;
